import { getProjectDetailApi } from "network/api/project";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiResType } from "types/enum";
import { ProjectDetailD, ProjectItemD } from "types/project";
import { log } from "utils/helpers";
import { useAuth } from "./AuthContext";

type MProjectItemD = ProjectDetailD & {
  loading?: boolean;
  ProjectUsage: {
    document_count: number;
    next_cycle: number;
    plan: "starter_monthly" | "growth_monthly" | "professional_monthly" | "basic";
    query_count: number;
  };
};

type ProjectContextD = {
  projectUid: any;
  activeProject: MProjectItemD;
  setActiveProject: React.Dispatch<React.SetStateAction<MProjectItemD>>;
};

const ProjectContext = createContext<ProjectContextD>({} as ProjectContextD);

export default function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { projectUid } = useParams();
  const { token } = useAuth();

  const [activeProject, setActiveProject] = useState<MProjectItemD>({} as MProjectItemD);

  const fetchProjectDetail = useCallback(async () => {
    try {
      if (!projectUid || !token) {
        return;
      }

      setActiveProject((s) => ({ ...s, loading: true }));

      const res = await getProjectDetailApi({ token, project_uid: projectUid });

      if (res.type === ApiResType.SUCCESS) {
        setActiveProject((s) => ({ ...s, ...res.data, loading: false }));
      }

      if (res.type === ApiResType.ERROR) {
        setActiveProject(res.data);
      }
    } catch (err) {
      setActiveProject((s) => ({ ...s, loading: false }));
    }
  }, [projectUid, token]);

  useEffect(() => {
    fetchProjectDetail();
  }, [fetchProjectDetail]);

  return <ProjectContext.Provider value={{ projectUid, activeProject, setActiveProject }}>{children}</ProjectContext.Provider>;
}

export const useProject = () => {
  return useContext(ProjectContext);
};
